import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from '@/pages/shared/base-query-auth';
import { runMutationOnce, runQueryOnce, stripEmptyParams } from '@/pages/shared/rtk-query-helpers';

/**
 * The small single-purpose admin pages — check files, check for locked PDFs, rebuild
 * categories, rename and the number type generator — all talk to the same
 * `/api/utilities` controller, so they share one api slice rather than registering five
 * near-empty ones. The two "does this number already exist" checks reach outside that
 * controller but belong to the rename page and stay here with it.
 */
const toFormData = (fields) => {
    const formData = new FormData();

    Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
    });

    return formData;
};

export const utilitiesApi = createApi({
    reducerPath: 'utilitiesApi',
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        checkFiles: builder.mutation({
            query: (filters) => ({ url: '/api/utilities/checkFiles', method: 'POST', body: filters }),
        }),
        generateImages: builder.mutation({
            query: (filters) => ({ url: '/api/utilities/generateImages', method: 'POST', body: filters }),
        }),
        getLockedDocumentCount: builder.query({
            query: () => ({ url: '/api/utilities/getLockedDocumentCount', method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        checkLockedPDFs: builder.mutation({
            query: () => ({ url: '/api/utilities/CheckLockedPDFs', method: 'POST' }),
        }),
        rebuildCategories: builder.mutation({
            query: (siteId) => ({ url: `/api/utilities/rebuildCategories/${siteId}`, method: 'POST' }),
        }),
        renameDocument: builder.mutation({
            query: ({ number, newNumber, siteId }) => ({
                url: '/api/utilities/rename/document',
                method: 'POST',
                body: toFormData({ number, newNumber, siteId }),
            }),
        }),
        existDocumentNumber: builder.mutation({
            query: ({ documentNumber, siteId }) => ({
                url: '/api/document/documentNumberExist',
                method: 'POST',
                body: toFormData({ documentNumber, siteId }),
            }),
        }),
        existCriticaEquipmentNumber: builder.mutation({
            query: ({ number, accountId }) => ({
                url: '/api/criticalEquipment/criticalEquipmentNumberExist',
                method: 'POST',
                body: toFormData({ number, accountId }),
            }),
        }),
        batchDocumentRename: builder.mutation({
            query: ({ model, siteId }) => ({
                url: `/api/utilities/rename/batchDocumentRename/${siteId}`,
                method: 'POST',
                body: model,
            }),
        }),
        batchCsvRename: builder.mutation({
            query: ({ siteId, formData }) => ({
                url: `/api/utilities/rename/parseRenameCsv/${siteId}`,
                method: 'POST',
                body: formData,
            }),
        }),
        findNumbers: builder.mutation({
            query: (data) => ({ url: '/api/utilities/numbers/find', method: 'POST', body: data }),
        }),
        reserveNumbers: builder.mutation({
            query: (data) => ({ url: '/api/utilities/numbers/reserve', method: 'POST', body: data }),
            transformResponse: (data, meta) => ({
                data,
                headers: { 'content-disposition': meta?.response?.headers?.get('content-disposition') },
            }),
        }),
        getNumberTypesBySite: builder.query({
            query: (siteId) => ({ url: `/api/utilities/numberType/${siteId}`, method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        getNumberTypesFieldsByNumberTypeId: builder.query({
            query: (numberTypeId) => ({ url: `/api/utilities/numberTypesField/${numberTypeId}`, method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        getNumberTypeGeneratorSites: builder.query({
            query: () => ({ url: '/api/utilities/numberTypeGenerator/sites', method: 'GET' }),
            keepUnusedDataFor: 0,
        }),
        checkDocumentNumberAvailability: builder.query({
            query: ({ siteId, documentNumber }) => ({
                url: '/api/utilities/checkDocumentNumberAvailability',
                method: 'GET',
                params: stripEmptyParams({ siteId, documentNumber }),
            }),
            keepUnusedDataFor: 0,
        }),
    }),
});

/**
 * Runs a `utilitiesApi` query endpoint once and releases its cache subscription.
 */
export const runUtilitiesQuery = (dispatch, endpointName, arg) =>
    runQueryOnce(dispatch, utilitiesApi.endpoints[endpointName], arg);

/**
 * Runs a `utilitiesApi` mutation endpoint once and clears its tracked entry.
 */
export const runUtilitiesMutation = (dispatch, endpointName, arg) =>
    runMutationOnce(dispatch, utilitiesApi.endpoints[endpointName], arg);

export default utilitiesApi;
